import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  CircularProgress,
  Alert,
  Typography,
  Chip,
  Grid
} from '@mui/material';
import api from '../services/api';

export default function AppointmentFormDialog({ 
  open, 
  onClose, 
  onSuccess,
  professionals: professionalsProp,
  services: servicesProp,
  places: placesProp,
  prefillData = null,
  initialData = null,  // ✅ Nova prop para dados de reagendamento
  preselectedDate = null  // ✅ Data pré-selecionada ao clicar no botão + da coluna
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Usar dados recebidos via props (do BFF da página)
  const professionals = professionalsProp || [];
  const services = servicesProp || [];
  const places = placesProp || []; // Places contêm professionals[] aninhado
  
  // Form state - ✅ usar initialData se fornecido, ou preselectedDate para data
  const [formData, setFormData] = useState({
    professionalId: '',
    serviceId: '',
    placeId: '',
    date: preselectedDate 
      ? preselectedDate.toISOString().split('T')[0] 
      : new Date().toISOString().split('T')[0],
    selectedSlot: null,
    clientName: initialData?.clientName || prefillData?.clientName || '',
    clientPhone: initialData?.clientPhone || prefillData?.clientPhone || '',
    clientEmail: initialData?.clientEmail || prefillData?.clientEmail || '',
    clientNotes: initialData?.clientNotes || ''
  });
  
  // Available slots
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  
  // Filtered data
  const [filteredServices, setFilteredServices] = useState([]);
  const [filteredPlaces, setFilteredPlaces] = useState([]);

  // ✅ Atualizar data quando preselectedDate mudar (ao abrir dialog com data específica)
  useEffect(() => {
    if (preselectedDate) {
      setFormData(prev => ({
        ...prev,
        date: preselectedDate.toISOString().split('T')[0]
      }));
    }
  }, [preselectedDate]);

  useEffect(() => {
    if (formData.professionalId) {
      filterServicesForProfessional();
    }
  }, [formData.professionalId, places, services]);

  useEffect(() => {
    if (formData.professionalId && formData.serviceId) {
      filterPlacesForService();
    }
  }, [formData.professionalId, formData.serviceId, places]);

  // REMOVED: Auto-load slots on date change
  // Users will click "Buscar Horários" button manually

  const filterServicesForProfessional = () => {
    // Buscar todos os places onde o profissional atende
    const professionalLinks = places.flatMap(place => 
      (place.professionals || []).filter(pp => pp.professionalId === formData.professionalId)
    );

    // Extrair serviceIds únicos dos vínculos
    const serviceIds = new Set();
    professionalLinks.forEach(pp => {
      if (pp.serviceIds && Array.isArray(pp.serviceIds)) {
        pp.serviceIds.forEach(id => serviceIds.add(id));
      }
    });

    // Filtrar serviços
    const filtered = services.filter(s => serviceIds.has(s.id));
    setFilteredServices(filtered);
    
    // Reset service se não estiver mais válido
    if (formData.serviceId && !serviceIds.has(formData.serviceId)) {
      setFormData(prev => ({ ...prev, serviceId: '', placeId: '', selectedSlot: null }));
      setAvailableSlots([]);
    }
  };

  const filterPlacesForService = () => {
    // Buscar places onde o profissional atende E oferece o serviço selecionado
    const validPlaces = places.filter(place => {
      const professionalLink = (place.professionals || []).find(
        pp => pp.professionalId === formData.professionalId &&
              pp.serviceIds?.includes(formData.serviceId)
      );
      return professionalLink !== undefined;
    });

    setFilteredPlaces(validPlaces);
    
    // Reset place se não estiver mais válido
    const validPlaceIds = new Set(validPlaces.map(p => p.id));
    if (formData.placeId && !validPlaceIds.has(formData.placeId)) {
      setFormData(prev => ({ ...prev, placeId: '', selectedSlot: null }));
      setAvailableSlots([]);
    }
  };

  const loadAvailableSlots = async () => {
    try {
      setLoadingSlots(true);
      setError(null);

      const response = await api.get('/appointments/available-slots', {
        params: {
          professionalId: formData.professionalId,
          serviceId: formData.serviceId,
          placeId: formData.placeId,
          date: formData.date
        }
      });

      setAvailableSlots(response.data || []);
      
      // Reset selected slot se não estiver mais disponível
      if (formData.selectedSlot) {
        const slotStillAvailable = (response.data || []).some(
          s => new Date(s.startTime).getTime() === new Date(formData.selectedSlot.startTime).getTime()
        );
        if (!slotStillAvailable) {
          setFormData(prev => ({ ...prev, selectedSlot: null }));
        }
      }
    } catch (err) {
      console.error('Erro ao carregar horários disponíveis:', err);
      setError('Erro ao carregar horários disponíveis');
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Reset campos dependentes
    if (field === 'professionalId') {
      setFormData(prev => ({ 
        ...prev, 
        [field]: value,
        serviceId: '', 
        placeId: '', 
        selectedSlot: null 
      }));
      setAvailableSlots([]);
    } else if (field === 'serviceId') {
      setFormData(prev => ({ 
        ...prev, 
        [field]: value,
        placeId: '', 
        selectedSlot: null 
      }));
      setAvailableSlots([]);
    } else if (field === 'placeId' || field === 'date') {
      setFormData(prev => ({ ...prev, [field]: value, selectedSlot: null }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.selectedSlot) {
      setError('Selecione um horário disponível');
      return;
    }

    if (!formData.clientName.trim()) {
      setError('Nome do cliente é obrigatório');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await api.post('/appointments', {
        // Identificadores para LOCALIZAR o slot na grade dinâmica
        professionalId: formData.professionalId,
        placeId: formData.placeId,
        scheduleId: formData.selectedSlot.scheduleId,
        serviceId: formData.serviceId,
        startTime: formData.selectedSlot.startTime, // Já vem em UTC do backend
        // DurationMinutes virá do slot retornado pelo backend
        // Dados do cliente
        clientName: formData.clientName,
        clientPhone: formData.clientPhone || null,
        clientEmail: formData.clientEmail || null,
        clientNotes: formData.clientNotes || null
      });

      onSuccess();
    } catch (err) {
      console.error('Erro ao criar agendamento:', err);
      setError(err.response?.data?.message || 'Erro ao criar agendamento');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        professionalId: '',
        serviceId: '',
        placeId: '',
        date: new Date().toISOString().split('T')[0],
        selectedSlot: null,
        clientName: '',
        clientPhone: '',
        clientEmail: '',
        clientNotes: ''
      });
      setAvailableSlots([]);
      setError(null);
      onClose();
    }
  };

  const formatSlotTime = (slot) => {
    const date = new Date(slot.startTime);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const canSubmit = formData.selectedSlot && formData.clientName.trim() && !loading;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Novo Agendamento</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          {/* Step 1: Professional */}
          <TextField
            select
            fullWidth
            label="Profissional"
            value={formData.professionalId}
            onChange={(e) => handleChange('professionalId', e.target.value)}
            disabled={loading}
            sx={{ mb: 2 }}
          >
            {Array.isArray(professionals) && professionals.map((prof) => (
              <MenuItem key={prof.id} value={prof.id}>
                {prof.name} {prof.specialty && `- ${prof.specialty}`}
              </MenuItem>
            ))}
          </TextField>

          {/* Step 2: Service */}
          {formData.professionalId && (
            <TextField
              select
              fullWidth
              label="Serviço"
              value={formData.serviceId}
              onChange={(e) => handleChange('serviceId', e.target.value)}
              disabled={loading || filteredServices.length === 0}
              helperText={filteredServices.length === 0 ? 'Nenhum serviço disponível para este profissional' : ''}
              sx={{ mb: 2 }}
            >
              {Array.isArray(filteredServices) && filteredServices.map((service) => (
                <MenuItem key={service.id} value={service.id}>
                  {service.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          {/* Step 3: Place */}
          {formData.serviceId && (
            <TextField
              select
              fullWidth
              label="Local"
              value={formData.placeId}
              onChange={(e) => handleChange('placeId', e.target.value)}
              disabled={loading || filteredPlaces.length === 0}
              helperText={filteredPlaces.length === 0 ? 'Este profissional não atende este serviço em nenhum local' : ''}
              sx={{ mb: 2 }}
            >
              {Array.isArray(filteredPlaces) && filteredPlaces.map((place) => (
                <MenuItem key={place.id} value={place.id}>
                  {place.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          {/* Step 4: Date */}
          {formData.placeId && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                type="date"
                label="Data"
                value={formData.date}
                onChange={(e) => handleChange('date', e.target.value)}
                disabled={loading}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 
                  lang: 'pt-BR',
                  style: { colorScheme: 'light' }
                }}
              />
              <Button
                variant="contained"
                onClick={loadAvailableSlots}
                disabled={loading || loadingSlots || !formData.date}
                sx={{ minWidth: '140px', whiteSpace: 'nowrap' }}
              >
                {loadingSlots ? 'Buscando...' : 'Buscar Horários'}
              </Button>
            </Box>
          )}

          {/* Step 5: Available Slots */}
          {formData.date && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Horários Disponíveis
              </Typography>
              {loadingSlots ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : availableSlots.length === 0 ? (
                <Alert severity="info">
                  Nenhum horário disponível para esta data
                </Alert>
              ) : (
                <Grid container spacing={1}>
                  {Array.isArray(availableSlots) && availableSlots.map((slot, index) => (
                    <Grid item xs={4} sm={3} md={2} key={index}>
                      <Chip
                        label={formatSlotTime(slot)}
                        onClick={() => setFormData(prev => ({ ...prev, selectedSlot: slot }))}
                        color={
                          formData.selectedSlot?.startTime === slot.startTime
                            ? 'primary'
                            : 'default'
                        }
                        variant={
                          formData.selectedSlot?.startTime === slot.startTime
                            ? 'filled'
                            : 'outlined'
                        }
                        sx={{ width: '100%', cursor: 'pointer' }}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}

          {/* Step 6: Client Info */}
          {formData.selectedSlot && (
            <>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Dados do Cliente
              </Typography>
              
              <TextField
                fullWidth
                label="Nome do Cliente *"
                value={formData.clientName}
                onChange={(e) => handleChange('clientName', e.target.value)}
                disabled={loading}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Telefone"
                placeholder="(00) 00000-0000"
                value={formData.clientPhone}
                onChange={(e) => {
                  // Aplica máscara brasileira
                  let value = e.target.value.replace(/\D/g, '');
                  if (value.length <= 10) {
                    value = value.replace(/^(\d{2})(\d{4})(\d{0,4}).*/, '($1) $2-$3');
                  } else {
                    value = value.replace(/^(\d{2})(\d{5})(\d{0,4}).*/, '($1) $2-$3');
                  }
                  handleChange('clientPhone', value.trim());
                }}
                disabled={loading}
                inputProps={{ maxLength: 15 }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="E-mail"
                type="email"
                value={formData.clientEmail}
                onChange={(e) => handleChange('clientEmail', e.target.value)}
                disabled={loading}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Observações"
                multiline
                rows={3}
                value={formData.clientNotes}
                onChange={(e) => handleChange('clientNotes', e.target.value)}
                disabled={loading}
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit}
        >
          {loading ? <CircularProgress size={24} /> : 'Criar Agendamento'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
